import { create } from 'zustand';

export type ToastTone = 'default' | 'success' | 'error';
export interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
}

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export interface DialogAction<T> {
  label: string;
  value: T;
  variant?: ButtonVariant;
}
export interface DialogRequest {
  title: string;
  message?: string;
  actions: DialogAction<unknown>[];
  resolve: (value: unknown) => void;
}

interface FeedbackState {
  toast: Toast | null;
  dialog: DialogRequest | null;
}

export const useFeedback = create<FeedbackState>(() => ({ toast: null, dialog: null }));

let toastId = 0;
let timer: ReturnType<typeof setTimeout> | undefined;

/** One toast at a time; auto-hides after 2.8 s (5.2 s for errors). docs/spec/06 §3. */
export function toast(message: string, tone: ToastTone = 'default'): void {
  clearTimeout(timer);
  const id = ++toastId;
  useFeedback.setState({ toast: { id, message, tone } });
  timer = setTimeout(
    () => {
      if (useFeedback.getState().toast?.id === id) useFeedback.setState({ toast: null });
    },
    tone === 'error' ? 5200 : 2800,
  );
}

export function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

/** Modal choice; scrim / Escape resolve null. Opening another dialog resolves the previous with null. */
export function ask<T>(req: { title: string; message?: string; actions: DialogAction<T>[] }): Promise<T | null> {
  useFeedback.getState().dialog?.resolve(null);
  return new Promise<T | null>((resolve) => {
    useFeedback.setState({
      dialog: {
        ...req,
        actions: req.actions as DialogAction<unknown>[],
        resolve: (v) => {
          useFeedback.setState({ dialog: null });
          resolve(v as T | null);
        },
      },
    });
  });
}
